import { Injectable } from '@angular/core';
import * as _ from 'lodash';

import { AvailableLanguage, getItemAsJson, setItemAsJson } from 'helpers';
import { BehaviorSubject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { OrganizationType, User } from './authenticatedUser/types';
import { EnhancedSigninInfo } from 'src/app/authentication/authentication.model';
import { AuthService, User as Auth0User } from '@auth0/auth0-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const userStorageKey = 'user';

@Injectable()
export class UserService {
    private auth0User$ = new BehaviorSubject<Auth0User>(null);
    private accessToken$ = new BehaviorSubject<string>(null);
    private userSwitchOrganization$ = new BehaviorSubject<boolean>(false);
    private userSubject = new BehaviorSubject<User>(this.user);
    readonly user$ = this.userSubject.asObservable();

    constructor(private auth0Service: AuthService) {
        // user$ emits when the user is logged in or the access token is refreshed
        const auth0UserChanged = this.auth0Service.user$.pipe(
            takeUntilDestroyed(),
            filter((user) => !!user)
        );

        auth0UserChanged.subscribe(this.auth0User$);

        auth0UserChanged
            .pipe(switchMap((_user) => this.auth0Service.getAccessTokenSilently()))
            .subscribe(this.accessToken$);

        this.auth0Service.appState$.subscribe((appState) => this.userSwitchOrganization$.next(!!appState?.switchOrgs));
    }

    getAuthToken() {
        // During the impersonation flow we return the access token from the user profile in local storage
        if (this.auth0User$.value && this.auth0User$.value.org_type === OrganizationType.Admin) {
            return this.user.accessToken;
        }

        return this.accessToken$.getValue();
    }

    getEmail() {
        return this.user.email;
    }

    getBrands() {
        return this.user.organization.brands;
    }

    addBrand(brand) {
        const newBrand: any = {
            name: brand.name,
            _id: brand._id
        };
        const user = this.user;
        user.organization.brands.push(newBrand);
        this.setCookie(user);
    }

    updateBrand(brand) {
        const index = _.findIndex(this.user.organization.brands, (b: any) => b._id && b._id === brand._id);

        if (index > -1) {
            const user = this.user;
            user.organization.brands[index] = {
                _id: brand._id,
                name: brand.name
            };

            this.setCookie(user);
        }
    }

    removeBrand(brandId) {
        const user = this.user;
        _.remove(user.organization.brands, (brand: any) => brand._id === brandId);
        this.setCookie(user);
    }

    getNumBrands() {
        return this.user.organization.brands.length;
    }

    getUserId() {
        return this.user._id;
    }

    getOrganizationId() {
        return this.user.organization._id;
    }

    getOrganization() {
        return this.user.organization;
    }

    getPlan() {
        return this.user.organization.plan;
    }

    getPrivileges(): string[] {
        return this.user.privileges;
    }

    public isUserProfileLoaded(): boolean {
        const currentUser = this.user; // Accesses the private getter 'user'
        // Ensure currentUser and its organization property (which contains 'plan') exist.
        // Adjust checks based on truly essential fields for a "loaded" profile.
        return !!currentUser && !!currentUser._id && !!currentUser.organization && !!currentUser.organization._id;
    }

    setOrganization(organization) {
        const user = this.user;
        user.organization = organization;
        this.setCookie(user);
    }

    getOrganizationType() {
        return this.user?.organization?.type;
    }

    isManufacturer() {
        return this.getOrganizationType() === OrganizationType.Manufacturer || this.isAdmin();
    }

    isRetailer() {
        return this.getOrganizationType() === OrganizationType.Retailer || this.isAdmin();
    }

    isAdmin() {
        return this.getOrganizationType() === OrganizationType.Admin;
    }

    isAppUser() {
        return !this.getOrganizationType();
    }

    hasAdminPrivilege() {
        const userPrivileges = this.getPrivileges();
        return _.includes(userPrivileges, 'admin');
    }

    hasAtLeastOnePrivilege(targetPrivileges: string[]): boolean {
        return _.some(this.user.privileges, (privilege) => _.includes(targetPrivileges, privilege));
    }

    hasUserSwitchedOrganization(): boolean {
        const currentUser = this.user;
        return (
            !!this.userSwitchOrganization$.value &&
            this.auth0User$.value?.platform_org_id !== currentUser.organization._id
        );
    }

    getPreferredLanguage() {
        if (this.user) {
            return this.user.preferredLanguage;
        }
    }

    setPreferredLanguage(language: AvailableLanguage) {
        const user = this.user;
        user.preferredLanguage = language;
        this.setCookie(user);
    }

    removeUser() {
        this.setCookie({} as any);
    }

    getRequestMarketingFilesPackage() {
        return _.get(this.user, 'organization.packages.requestMarketingFiles', false);
    }

    hasValidOrganizationProfile(): boolean {
        const organization: any = _.pick(this.getOrganization(), ['name', 'address']);
        const requiredProfileFields: string[] = [
            'name',
            'address',
            'address.line1',
            'address.zip',
            'address.city',
            'address.country'
        ];

        return _.every(
            requiredProfileFields,
            (requiredProfileField) => !_.isEmpty(_.get(organization, requiredProfileField))
        );
    }

    isLoggedIn() {
        return this.getAuthToken() !== null;
    }

    prepareEnhancedSigninInfo(signinInfo: EnhancedSigninInfo): User {
        return {
            ...signinInfo.user,
            accessToken: signinInfo.user.accessToken,
            preferredLanguage: signinInfo.user.preferredLanguage as AvailableLanguage,
            type: signinInfo.user.organization.type,
            plan: signinInfo.user.organization.plan,
            email: signinInfo.user.email,
            refreshToken: '' // TODO: Check if this is still needed
        };
    }

    setUser() {
        this.userSubject.next(this.user);
    }

    private setCookie(newUser) {
        setItemAsJson(userStorageKey, newUser);
        this.userSubject.next(newUser);
    }

    private get user() {
        return getItemAsJson(userStorageKey) as User;
    }
}
